import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';

import { ENUM_ICONS, formatEnumLabel, getEnumThumbnail } from '../meta';
import type {
  ActionValue,
  AssetSummary,
  MediaValue,
  PropSchemaProperty,
  ScreenOption,
  ThemeTokens,
} from '../types';
import ActionPickerField from './ActionPickerField';
import FieldShell from './FieldShell';
import IconToggleField from './IconToggleField';
import MediaPickerField from './MediaPickerField';
import type { MultiSelectOption } from './MultiSelectField';
import EventFiltersField from './EventFiltersField';
import RepeaterField from './RepeaterField';
import SegmentedField from './SegmentedField';
import SwatchPickerField from './SwatchPickerField';
import TextareaField from './TextareaField';
import TextInputField from './TextInputField';
import ThumbnailPickerField from './ThumbnailPickerField';
import ToggleField from './ToggleField';

export interface SchemaFieldsContext {
  /** Theme tokens for token-ref swatches. */
  tokens?: ThemeTokens;
  /** Screens an action's internal link may target. */
  screenOptions?: ScreenOption[];
  /** Venue options for query-filters fields (live tenant data). */
  venueOptions?: MultiSelectOption[];
  /** Resolves `asset:<id>` refs for the media picker. */
  resolveAssetRef?: (ref: string) => AssetSummary | undefined;
  /** Opens the asset library for the named prop; selection flows back via onChange. */
  onOpenAssetLibrary?: (propName: string) => void;
}

interface SchemaFieldsProps extends SchemaFieldsContext {
  properties: Record<string, PropSchemaProperty>;
  values: Record<string, unknown>;
  /** Only render props belonging to this accordion group (undefined = all). */
  group?: string;
  onChange: (propName: string, value: unknown) => void;
}

/** Effective value: authored value, else schema default. */
function effectiveValue(
  values: Record<string, unknown>,
  name: string,
  property: PropSchemaProperty,
): unknown {
  return values[name] !== undefined ? values[name] : property.default;
}

/** Every showIf pair must match the effective value of the referenced prop. */
function isVisible(
  property: PropSchemaProperty,
  properties: Record<string, PropSchemaProperty>,
  values: Record<string, unknown>,
): boolean {
  if (!property.showIf) return true;
  return Object.entries(property.showIf).every(([dep, expected]) => {
    const depSchema = properties[dep];
    const actual = depSchema
      ? effectiveValue(values, dep, depSchema)
      : values[dep];
    return actual === expected;
  });
}

/** Build a fresh repeater item from its schema defaults. */
function itemDefaults(property: PropSchemaProperty): Record<string, unknown> {
  const item: Record<string, unknown> = {};
  Object.entries(property.items?.properties ?? {}).forEach(([key, sub]) => {
    if (sub.default !== undefined) item[key] = sub.default;
  });
  return item;
}

/**
 * Authored identity for a repeater row's header: the item's own label/title
 * prop when set (a button row reads "Book Now", not "Button 1").
 */
function itemTitle(item: Record<string, unknown>): string | undefined {
  const candidate = item.label ?? item.title;
  return typeof candidate === 'string' ? candidate : undefined;
}

function Field({
  name,
  property,
  value,
  onChange,
  context,
}: {
  name: string;
  property: PropSchemaProperty;
  value: unknown;
  onChange: (value: unknown) => void;
  context: SchemaFieldsContext;
}) {
  const { title, description, placeholder } = property;

  /* format refines the control first */
  switch (property.format) {
    case 'textarea':
      return (
        <TextareaField
          title={title}
          description={description}
          placeholder={placeholder}
          value={value as string | undefined}
          onChange={onChange}
        />
      );
    case 'thumbnails':
      return (
        <ThumbnailPickerField
          title={title}
          description={description}
          options={(property.enum ?? []).map((v) => ({
            value: v,
            label: formatEnumLabel(v),
            preview: getEnumThumbnail(v),
          }))}
          value={value as string | undefined}
          onChange={onChange}
        />
      );
    case 'icon-toggle':
      return (
        <IconToggleField
          title={title}
          description={description}
          options={(property.enum ?? []).map((v) => ({
            value: v,
            label: formatEnumLabel(v),
            icon: ENUM_ICONS[v],
          }))}
          value={value as string | undefined}
          onChange={onChange}
        />
      );
    case 'asset-ref':
      return (
        <MediaPickerField
          title={title}
          description={description}
          value={value as MediaValue | undefined}
          resolveAssetRef={context.resolveAssetRef}
          onOpenLibrary={() => context.onOpenAssetLibrary?.(name)}
          onChange={onChange}
        />
      );
    case 'action':
      return (
        <ActionPickerField
          title={title}
          description={description}
          value={value as ActionValue | undefined}
          screenOptions={context.screenOptions ?? []}
          onChange={onChange}
        />
      );
    case 'query-filters':
      // The format is generic; the schema's `filterSet` names the
      // vocabulary. Only 'events' exists today — an unknown set (a newer
      // kit's block in an older admin) renders nothing, fail-safe.
      if (property.filterSet !== 'events') return null;
      return (
        <EventFiltersField
          title={title}
          description={description}
          value={value}
          venueOptions={context.venueOptions}
          onChange={onChange}
        />
      );
    case 'token-ref':
      return (
        <SwatchPickerField
          title={title}
          description={description}
          tokens={
            property.tokenGroup === 'color' || !property.tokenGroup
              ? (context.tokens?.color ?? {})
              : {}
          }
          value={value as string | undefined}
          onChange={onChange}
        />
      );
    case 'repeater':
      return (
        <RepeaterField
          title={title}
          description={description}
          itemLabel={property.itemLabel}
          items={(value as Record<string, unknown>[] | undefined) ?? []}
          maxItems={property.maxItems}
          getItemTitle={itemTitle}
          createItem={() => itemDefaults(property)}
          renderItem={(item, onItemChange) => (
            <SchemaFields
              properties={property.items?.properties ?? {}}
              values={item}
              onChange={(subName, subValue) =>
                onItemChange({ ...item, [subName]: subValue })
              }
              {...context}
            />
          )}
          onChange={onChange}
        />
      );
  }

  /* then type picks the base control */
  if (property.type === 'boolean') {
    return (
      <ToggleField
        title={title}
        description={description}
        value={value as boolean | undefined}
        onChange={onChange}
      />
    );
  }

  if (property.enum) {
    if (property.enum.length <= 4) {
      return (
        <SegmentedField
          title={title}
          description={description}
          options={property.enum.map((v) => ({
            value: v,
            label: formatEnumLabel(v),
          }))}
          value={value as string | undefined}
          onChange={onChange}
        />
      );
    }
    return (
      <FieldShell title={title} description={description}>
        <Select value={value as string | undefined} onValueChange={onChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {property.enum.map((v) => (
              <SelectItem key={v} value={v}>
                {formatEnumLabel(v)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldShell>
    );
  }

  return (
    <TextInputField
      title={title}
      description={description}
      placeholder={placeholder}
      value={value as string | undefined}
      onChange={onChange}
    />
  );
}

/**
 * Renders the controls for a prop schema — the settings panel is generated,
 * never hand-built per block. Skips `editable: false` props and props whose
 * `showIf` condition is not met.
 */
export default function SchemaFields({
  properties,
  values,
  group,
  onChange,
  ...context
}: SchemaFieldsProps) {
  const visible = Object.entries(properties).filter(
    ([, property]) =>
      property.editable !== false &&
      (group === undefined || (property.group ?? 'Content') === group) &&
      isVisible(property, properties, values),
  );

  return (
    <>
      {visible.map(([name, property]) => (
        <Field
          key={name}
          name={name}
          property={property}
          value={effectiveValue(values, name, property)}
          onChange={(value) => {
            // Enforce mutual exclusivity: enabling this prop disables the
            // props it is declared exclusive with.
            if (value === true) {
              property.exclusiveWith?.forEach((other) => {
                if (effectiveValue(values, other, properties[other] ?? {})) {
                  onChange(other, false);
                }
              });
            }
            onChange(name, value);
          }}
          context={context}
        />
      ))}
    </>
  );
}
